import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AccountType,
  Contact,
  ContactExtended,
} from '../../../../../../types/api';
import { INSTALLER_TYPES_LABELS } from '../../../constants';
import { useAuth } from '../../../hooks';
import { formatDate } from '../../../utils/formatters';
import { formatPhoneNumber } from '../../loan_application/utils/helpers';
import ContactRowActionMenu from './ContactRowActionMenu';
import ContactStatusChip from './ContactStatusChip';

type Props = {
  data: ContactExtended[];
  isLoading: boolean;
  setModalState: (state: { open: boolean; contact?: Contact }) => void;
};

export default function AccountContactsTable({
  data,
  isLoading,
  setModalState,
}: Props) {
  const theme = useTheme();
  const { user } = useAuth();

  return (
    <Box
      sx={{
        overflowX: 'auto',
        borderBottomLeftRadius: 16,
        borderBottomRightRadius: 16,
      }}
    >
      <Table sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow
            sx={{
              '& > th': {
                fontWeight: 700,
                px: 1,
                py: 0,
                borderTop: `1px solid ${theme.palette.border.input}`,
                borderBottom: isLoading
                  ? `1px solid ${theme.palette.border.input}`
                  : 0,
              },
            }}
          >
            <TableCell sx={{ width: '15%' }}>
              <Box sx={{ pl: 1 }}>Name</Box>
            </TableCell>
            <TableCell sx={{ width: '30%' }}>Email</TableCell>
            <TableCell sx={{ width: '10%', textAlign: 'center' }}>
              Phone
            </TableCell>
            {user?.account.type === AccountType.INSTALLER && (
              <TableCell sx={{ width: '10%', textAlign: 'center' }}>
                Role
              </TableCell>
            )}
            <TableCell sx={{ width: '10%', textAlign: 'center' }}>
              Status
            </TableCell>
            <TableCell sx={{ width: '10%', textAlign: 'center' }}>
              Last Login
            </TableCell>
            <TableCell sx={{ width: '10%', textAlign: 'center' }}>
              Added By
            </TableCell>
            <TableCell sx={{ width: '5%' }} />
          </TableRow>
        </TableHead>

        <TableBody>
          {data.map((contact) => (
            <TableRow
              key={contact.id}
              sx={{
                '& > td': {
                  borderTop: `1px solid ${theme.palette.border.input}`,
                  borderBottom: 0,
                  overflowWrap: 'anywhere',
                  textOverflow: 'ellipsis',
                  whiteSpaceTrim: 'trim',
                  overflow: 'hidden',
                  p: 1,
                },
                '& > td:last-of-type': {
                  paddingRight: 0,
                  display: 'flex',
                  justifyContent: 'flex-end',
                },
              }}
            >
              <TableCell>
                <Box display="flex" flexDirection="column" sx={{ pl: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {contact.name}
                  </Typography>
                  {contact.position_title ? (
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {contact.position_title}
                    </Typography>
                  ) : null}
                </Box>
              </TableCell>

              <TableCell sx={{ color: 'text.secondary' }}>
                <Typography variant="body2" color="text.secondary">
                  {contact.email}
                </Typography>
              </TableCell>

              <TableCell sx={{ color: 'text.secondary', textAlign: 'center' }}>
                {formatPhoneNumber(contact.phone ?? contact.mobile_phone) ??
                  contact.phone ??
                  contact.mobile_phone ??
                  '-'}
              </TableCell>

              {user?.account.type === AccountType.INSTALLER && (
                <TableCell sx={{ textAlign: 'center' }}>
                  {contact.installer_role
                    ? INSTALLER_TYPES_LABELS[contact.installer_role]
                    : '-'}
                </TableCell>
              )}

              <TableCell sx={{ textAlign: 'center' }}>
                <ContactStatusChip status={contact.status} />
              </TableCell>

              <TableCell sx={{ color: 'text.secondary', textAlign: 'center' }}>
                {contact.last_login ? formatDate(contact.last_login) : '-'}
              </TableCell>

              <TableCell sx={{ color: 'text.secondary', textAlign: 'center' }}>
                {contact.created_by_user_contact?.name ?? '-'}
              </TableCell>

              <TableCell>
                <ContactRowActionMenu
                  contact={contact}
                  setModalState={setModalState}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {isLoading &&
        new Array(3)
          .fill(null)
          .map((_, i) => (
            <Skeleton
              variant="rectangular"
              width="96%"
              height={70}
              sx={{ my: 1, borderRadius: '12px', mx: 'auto' }}
              key={i}
            />
          ))}
    </Box>
  );
}
